import { writable, derived } from 'svelte/store';

const TIMEOUT = 3000;

/**
 * @typedef {{ id: string; variant: string; message: string; timeout: number }} ToastAlert
 * @param {number} [timeout]
 */
function createToastAlertStore(timeout) {
	/** @type {import('svelte/store').Writable<ToastAlert[]>} */
	const _toastAlerts = writable([]);

	/**
	 * @param {string} message
	 * @param {string} variant
	 * @param {number} timeout
	 */
	function send(message, variant = 'info', timeout = TIMEOUT) {
		if (timeout === undefined) {
			timeout = TIMEOUT;
		}
		_toastAlerts.update((state) => {
			return [...state, { id: id(), variant, message, timeout }];
		});
	}

	let timers = [];

	const toastAlerts = derived(_toastAlerts, ($_toastAlerts, set) => {
		set($_toastAlerts);
		if ($_toastAlerts.length > 0) {
			const timer = setTimeout(() => {
				_toastAlerts.update((state) => {
					state.shift();
					return state;
				});
			}, $_toastAlerts[0].timeout);
			return () => {
				clearTimeout(timer);
			};
		}
	});
	const { subscribe } = toastAlerts;

	return {
		subscribe,
		send,
		/**
		 * @param {string} msg
		 * @param {number} [timeout]
		 */
		default: (msg, timeout) => send(msg, 'default', timeout),
		/**
		 * @param {string} msg
		 * @param {number} [timeout]
		 */
		info: (msg, timeout) => send(msg, 'info', timeout),
		/**
		 * @param {string} msg
		 * @param {number} [timeout]
		 */
		success: (msg, timeout) => send(msg, 'success', timeout),
		/**
		 * @param {string} msg
		 * @param {number} [timeout]
		 */
		warning: (msg, timeout) => send(msg, 'warning', timeout),
		/**
		 * @param {string} msg
		 * @param {number} [timeout]
		 */
		error: (msg, timeout) => send(msg, 'error', timeout)
	};
}

function id() {
	return '_' + Math.random().toString(36).substr(2, 9);
}

export const toastAlertStore = createToastAlertStore();
